import { Directive, Input, OnInit, OnChanges, SimpleChanges, OnDestroy, inject, Signal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { NavigationActionService } from '../../services/navigation-action.service';
import { Subscription } from 'rxjs';

/**
 * Directive to abstract away common navigation action button functionality
 *
 * Usage:
 * ```html
 * <form [formGroup]="myForm" appSaveButton
 *       [saveForm]="myForm"
 *       [isLoading]="isSubmitting"
 *       [buttonLabel]="'Save'"
 *       [testId]="'my-component__save-button'"
 *       [saveAction]="handleSubmit.bind(this)">
 * </form>
 * ```
 */
@Directive({
  selector: '[appSaveButton]',
  standalone: true
})
export class SaveButtonDirective implements OnInit, OnChanges, OnDestroy {
  @Input() saveForm!: FormGroup;
  @Input() isLoading: boolean | Signal<boolean> = false;
  @Input() buttonLabel: string = 'Save';
  @Input() testId: string = 'save-button';
  @Input() saveAction!: () => void;
  @Input() disableCondition?: () => boolean;

  private navigationActionService = inject(NavigationActionService);
  private subscriptions = new Subscription();
  private currentFormInstance: FormGroup | null = null;

  ngOnInit(): void {
    if (!this.saveAction) {
      console.error('SaveButtonDirective: No save action provided');
      return;
    }

    this.setupFormSubscriptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If the form instance has changed, set up new subscriptions
    if (changes['saveForm'] && this.saveForm && this.saveForm !== this.currentFormInstance) {
      this.setupFormSubscriptions();
    }

    // Update button state when isLoading changes
    if (changes['isLoading']) {
      this.updateSaveButtonState();
    }
  }

  ngOnDestroy(): void {
    this.navigationActionService.clearMiddleAction();
    this.subscriptions.unsubscribe();
  }

  private setupFormSubscriptions(): void {
    // Clean up previous subscriptions
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();

    // Store reference to current form instance
    this.currentFormInstance = this.saveForm;

    if (!this.saveForm) {
      console.warn('SaveButtonDirective: No form provided or form is empty');
      return;
    }

    // Setup the initial button state
    this.updateSaveButtonState();

    // Subscribe to form value changes
    this.subscriptions.add(
      this.saveForm.valueChanges.subscribe(() => {
        this.updateSaveButtonState();
      })
    );

    // Subscribe to form status changes
    this.subscriptions.add(
      this.saveForm.statusChanges.subscribe(() => {
        this.updateSaveButtonState();
      })
    );
  }

  private updateSaveButtonState(): void {
    if (!this.saveForm) return;

    let isDisabled = this.saveForm.invalid || !this.saveForm.dirty;

    // Apply additional disable condition if provided
    if (this.disableCondition) {
      isDisabled = isDisabled || this.disableCondition();
    }

    // Determine loading state
    let loading: boolean;
    if (typeof this.isLoading === 'function') {
      // If it's a signal, get its value
      loading = (this.isLoading as Signal<boolean>)();
    } else {
      // Otherwise use it directly
      loading = !!this.isLoading;
    }

    this.navigationActionService.setMiddleAction({
      label: this.buttonLabel,
      disabled: isDisabled,
      loading: loading,
      testId: this.testId,
      action: this.saveAction
    });
  }
}
